import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
  Stack,
  Card,
  CardContent,
  LinearProgress,
  Fade,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import { EmailSendData } from '../hooks/useSendEmail';

type EmailResponseDataInput = {
  open:boolean;
  onClose: () => void;
  data: EmailSendData | null;
  addVoucherCol: boolean;
}

const EmailResponseSummary = ({ open, onClose, data, addVoucherCol }:EmailResponseDataInput) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const errorColWidth = isMobile ? 160 : 320;

  const summary = data?.summary ?? { total: 0, success: 0, failed: 0 };
  const rawDetails = data?.details;
  const details = Array.isArray(rawDetails) ? rawDetails : [];

  const successRate = data ? ((summary.success || 0) / (summary.total || 1)) * 100 : 0;
  const failedRate = data ? ((summary.failed || 0) / (summary.total || 1)) * 100 : 0;

  const headerCellStyle = {
    fontWeight: 700,
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText || '#fff',
    fontSize: 14,
    borderBottom: 'none',
    zIndex: 1,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    py: 1.5,
  };

  if(!data) {
    return null
  }

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minWidth: { xs: '100%', sm: 900 },
          maxWidth: 1300,
          minHeight: { xs: '90vh', sm: 700 },
          maxHeight: { xs: '90vh', sm: 700 },
          borderRadius: 4,
          p: 0,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          background: '#fff',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header with theme color */}
      <Box
        sx={{
          background: theme.palette.primary.main,
          color: theme.palette.primary.contrastText || '#fff',
          px: 4,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <EmailIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontSize: { xs: 20, sm: 24 },
                letterSpacing: 0.5,
              }}
            >
              Email Delivery Summary
            </Typography>
            <Typography
              variant="body2"
              sx={{
                opacity: 0.9,
                fontSize: 13,
                mt: 0.5,
              }}
            >
              Review the results of your email campaign
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: '#fff',
            background: 'rgba(255,255,255,0.2)',
            '&:hover': {
              background: 'rgba(255,255,255,0.3)',
              transform: 'rotate(90deg)',
              transition: 'all 0.3s ease',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column', background: '#f8f9fa' }}>
        {/* Summary Cards */}
        <Box sx={{ px: 4, pt: 2, pb: 1.5 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ mb: 1.5 }}
          >
            {/* Success Card */}
            <Fade in={true} timeout={500}>
              <Card
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  color: '#fff',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(17, 153, 142, 0.2)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(17, 153, 142, 0.3)',
                  },
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600, fontSize: 12 }}>
                      Successful
                    </Typography>
                    <CheckCircleRoundedIcon sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, fontSize: 24 }}>
                    {summary.success || 0}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={successRate}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.3)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#fff',
                        borderRadius: 2,
                      },
                    }}
                  />
                  <Typography variant="caption" sx={{ opacity: 0.8, mt: 0.5, display: 'block', fontSize: 11 }}>
                    {successRate.toFixed(1)}% success rate
                  </Typography>
                </CardContent>
              </Card>
            </Fade>

            {/* Failed Card */}
            <Fade in={true} timeout={700}>
              <Card
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
                  color: '#fff',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(235, 51, 73, 0.2)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(235, 51, 73, 0.3)',
                  },
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600, fontSize: 12 }}>
                      Failed
                    </Typography>
                    <ErrorRoundedIcon sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, fontSize: 24 }}>
                    {summary.failed || 0}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={failedRate}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.3)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#fff',
                        borderRadius: 2,
                      },
                    }}
                  />
                  <Typography variant="caption" sx={{ opacity: 0.8, mt: 0.5, display: 'block', fontSize: 11 }}>
                    {failedRate.toFixed(1)}% failure rate
                  </Typography>
                </CardContent>
              </Card>
            </Fade>

            {/* Total Card */}
            <Fade in={true} timeout={900}>
              <Card
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.3)',
                  },
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600, fontSize: 12 }}>
                      Total Sent
                    </Typography>
                    <SendIcon sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, fontSize: 24 }}>
                    {summary.total || 0}
                  </Typography>
                  <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.3)', mt: 1.5 }} />
                  <Typography variant="caption" sx={{ opacity: 0.8, mt: 0.5, display: 'block', fontSize: 11 }}>
                    Emails processed
                  </Typography>
                </CardContent>
              </Card>
            </Fade>
          </Stack>
        </Box>

        {/* Table Container */}
        <Box sx={{ px: 4, pb: 3, pt: 1, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid #e0e0e0',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <TableContainer
              sx={{
                flex: 1,
                maxHeight: '100%',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f5f5f5',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#bdbdbd',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#9e9e9e',
                  },
                },
              }}
            >
              <Table stickyHeader size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={headerCellStyle}>
                      #
                    </TableCell>
                    {addVoucherCol && (
                      <TableCell sx={headerCellStyle}>Voucher No#</TableCell>
                    )}
                    <TableCell sx={headerCellStyle}>Recipient Name</TableCell>
                    <TableCell sx={headerCellStyle}>Email</TableCell>
                    <TableCell align="center" sx={headerCellStyle}>
                      Status
                    </TableCell>
                    <TableCell
                      sx={{
                        ...headerCellStyle,
                        minWidth: errorColWidth,
                        maxWidth: errorColWidth,
                      }}
                    >
                      Error Message
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={addVoucherCol ? 6 : 5} align="center" sx={{ py: 8 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <EmailIcon sx={{ fontSize: 64, color: '#bdbdbd', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                            No data found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            There are no email details to display
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    details.map((row, idx) => (
                      <TableRow
                        key={idx}
                        sx={{
                          background: row?.status === 'Failed' 
                            ? 'linear-gradient(90deg, rgba(244, 67, 54, 0.05) 0%, rgba(244, 67, 54, 0.02) 100%)' 
                            : 'inherit',
                          borderLeft: row?.status === 'Failed' ? '4px solid #f44336' : '4px solid transparent',
                          transition: 'background 0.2s ease',
                          '&:hover': {
                            background: row?.status === 'Failed' 
                              ? 'linear-gradient(90deg, rgba(244, 67, 54, 0.08) 0%, rgba(244, 67, 54, 0.04) 100%)' 
                              : 'rgba(0, 0, 0, 0.02)',
                          },
                        }}
                      >
                        <TableCell align="center" sx={{ fontWeight: 600, color: '#666', py: 2 }}>
                          {idx + 1}
                        </TableCell>
                        {addVoucherCol && (
                          <TableCell sx={{ fontWeight: 500, py: 2 }}>
                            <Typography variant="body2" sx={{ color: '#424242' }}>
                              {row?.vouchernumber || '-'}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell sx={{ fontWeight: 500, py: 2 }}>
                          <Typography variant="body2" sx={{ color: '#424242' }}>
                            {row?.party || row?.recipient || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              color: '#555',
                              fontSize: 13,
                            }}
                          >
                            {row?.email || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          <Chip
                            icon={
                              row?.status === 'Success' ? (
                                <CheckCircleRoundedIcon fontSize="small" />
                              ) : (
                                <ErrorRoundedIcon fontSize="small" />
                              )
                            }
                            label={row?.status ?? '-'}
                            color={row?.status === 'Success' ? 'success' : 'error'}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              letterSpacing: 0.3,
                              fontSize: 12,
                              px: 1.5,
                              py: 0.5,
                              height: 28,
                              bgcolor: row?.status === 'Success' 
                                ? 'rgba(17, 153, 142, 0.1)' 
                                : 'rgba(244, 67, 54, 0.1)',
                              color: row?.status === 'Success' ? '#11998e' : '#f44336',
                              border: `1px solid ${row?.status === 'Success' ? 'rgba(17, 153, 142, 0.2)' : 'rgba(244, 67, 54, 0.2)'}`,
                              '& .MuiChip-icon': {
                                color: row?.status === 'Success' ? '#11998e' : '#f44336',
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            color: row?.status === 'Failed' ? '#d32f2f' : '#11998e',
                            fontWeight: 500,
                            minWidth: errorColWidth,
                            maxWidth: errorColWidth,
                            whiteSpace: 'pre-line',
                            wordBreak: 'break-word',
                            py: 2,
                            fontSize: 13,
                          }}
                        >
                          {row?.status === 'Failed' ? (
                            <Typography variant="body2" sx={{ color: '#d32f2f', lineHeight: 1.6 }}>
                              {row?.message || '-'}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ color: '#9e9e9e', fontStyle: 'italic' }}>
                              -
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EmailResponseSummary;
